import { useQuery } from "@tanstack/react-query";
import { Apple, ChefHat, ListChecks, ShoppingBasket } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { SubAreaCard } from "@/components/health/SubAreaCard";

// Tables added in BW12 aren't in the generated supabase types yet — fall back
// to the untyped builder pattern used throughout /finance + /meal-plan.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFrom = { from: (table: string) => any };

/**
 * BW12.5 Issue 2 — Kitchen hub.
 *
 * Single sidebar entry that fans out into the four food/meal-plan/pantry
 * surfaces. Reuses the SubAreaCard pattern from /health for visual parity.
 */

export function Kitchen() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const counts = useQuery({
    queryKey: ["kitchen_summary", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const today = new Date().toISOString().slice(0, 10);
      const in7 = new Date();
      in7.setDate(in7.getDate() + 7);
      const in7Iso = in7.toISOString().slice(0, 10);

      const supa = supabase as unknown as AnyFrom;
      const [pantryAll, pantryExpiring, recipes, plan, shoppingItems] =
        await Promise.all([
          supa
            .from("pantry_item")
            .select("id", { count: "exact", head: true })
            .eq("user_id", userId),
          supa
            .from("pantry_item")
            .select("id", { count: "exact", head: true })
            .eq("user_id", userId)
            .not("expires", "is", null)
            .gte("expires", today)
            .lte("expires", in7Iso),
          supa
            .from("recipe")
            .select("id, is_favorite", { count: "exact" })
            .eq("user_id", userId)
            .limit(200),
          supa
            .from("meal_plan_entry")
            .select("id", { count: "exact", head: true })
            .eq("user_id", userId)
            .gte("date", today)
            .lte("date", in7Iso),
          supa
            .from("shopping_list_item")
            .select("id", { count: "exact", head: true })
            .eq("user_id", userId)
            .is("checked_at", null),
        ]);

      const recipeRows = (recipes.data ?? []) as Array<{ is_favorite: boolean }>;
      const favorites = recipeRows.filter((r) => r.is_favorite).length;

      return {
        pantryTotal: pantryAll.count ?? 0,
        pantryExpiring: pantryExpiring.count ?? 0,
        recipeTotal: recipes.count ?? recipeRows.length ?? 0,
        recipeFavorites: favorites,
        planThisWeek: plan.count ?? 0,
        shoppingOpen: shoppingItems.count ?? 0,
      };
    },
  });

  const c = counts.data;

  return (
    <article aria-labelledby="kitchen-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Kitchen
      </p>
      <h1
        id="kitchen-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        Everything food.
      </h1>

      <div
        className="haus-kitchen-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "var(--space-4)",
        }}
      >
        <style>{`
          @media (max-width: 600px) {
            .haus-kitchen-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
        <SubAreaCard
          to="/pantry"
          label="Pantry"
          Icon={Apple}
          loading={counts.isLoading}
          headline={c ? `${c.pantryTotal} item${c.pantryTotal === 1 ? "" : "s"}` : null}
          subline={
            c
              ? c.pantryExpiring > 0
                ? `${c.pantryExpiring} expiring this week`
                : "Nothing expiring this week"
              : null
          }
        />
        <SubAreaCard
          to="/recipes"
          label="Recipes"
          Icon={ChefHat}
          loading={counts.isLoading}
          headline={c ? `${c.recipeTotal} recipe${c.recipeTotal === 1 ? "" : "s"}` : null}
          subline={
            c
              ? c.recipeFavorites > 0
                ? `${c.recipeFavorites} favorite${c.recipeFavorites === 1 ? "" : "s"}`
                : "Build your library"
              : null
          }
        />
        <SubAreaCard
          to="/meal-plan"
          label="Meal plan"
          Icon={ListChecks}
          loading={counts.isLoading}
          headline={
            c
              ? c.planThisWeek > 0
                ? `${c.planThisWeek} planned this week`
                : "Nothing planned"
              : null
          }
          subline={c?.planThisWeek === 0 ? "Auto-generate or add manually" : "View / edit"}
        />
        <SubAreaCard
          to="/shopping"
          label="Shopping"
          Icon={ShoppingBasket}
          loading={counts.isLoading}
          headline={
            c
              ? c.shoppingOpen > 0
                ? `${c.shoppingOpen} item${c.shoppingOpen === 1 ? "" : "s"} open`
                : "List clear"
              : null
          }
          subline={c?.shoppingOpen ? "Tap to check off" : "Generated from meal plan"}
        />
      </div>
    </article>
  );
}
